import { useState, useMemo, useEffect } from 'react';
import {
  useExpenseCategories,
  useExpenses,
  useMonthlyFinancialSummary,
  useMonthlyCashFlow,
  useYearlyFinancialSummary,
  useCreateExpense,
  useUpdateExpense,
  useDeleteExpense,
  useToggleExpensePaid,
  useCopyRecurringExpenses,
  useProductCostBreakdown,
  useSalesBreakdown,
  useWithdrawalBreakdown,
  useWithdrawalDetail,
  useServiceSalesBreakdown,
} from '@/hooks/useFinance';
import {
  FixedExpense,
  FixedExpenseCreate,
  MONTH_NAMES,
  CATEGORY_COLORS,
  financeService,
} from '@/services/finance';
import { paymentMethodLabel } from '@/services/cashRegister';
import { formatPrice } from '@/utils/format';
import { descargarCsv, fechaCsv, numeroCsv } from '@/utils/csv';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import DailyCategorySummary from '@/components/DailyCategorySummary';
import BillingSummary from '@/components/BillingSummary';
import { useAuthStore } from '@/context/authStore';
import {
  DollarSign,
  TrendingDown,
  Plus,
  Trash2,
  Edit2,
  ChevronLeft,
  ChevronRight,
  Copy,
  RefreshCw,
  Home,
  Zap,
  Wifi,
  Sparkles,
  Users,
  FileText,
  Shield,
  Wrench,
  MoreHorizontal,
  PiggyBank,
  AlertCircle,
  CheckCircle2,
  Clock,
  Stethoscope,
  Download,
} from 'lucide-react';
import clsx from 'clsx';

// Mapeo de iconos para categorías
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  Home: Home,
  Zap: Zap,
  Wifi: Wifi,
  Sparkles: Sparkles,
  Users: Users,
  FileText: FileText,
  Shield: Shield,
  Wrench: Wrench,
  MoreHorizontal: MoreHorizontal,
};

// SVG de animalitos para la barra visual
const CatIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 64 64" className={className}>
    {/* Orejas puntiagudas */}
    <polygon points="16,22 12,4 24,18" fill="#808080" />
    <polygon points="16,22 14,8 22,18" fill="#FFB6C1" />
    <polygon points="48,22 52,4 40,18" fill="#808080" />
    <polygon points="48,22 50,8 42,18" fill="#FFB6C1" />
    {/* Cabeza */}
    <ellipse cx="32" cy="32" rx="18" ry="16" fill="#A0A0A0" />
    {/* Mancha clara en cara */}
    <ellipse cx="32" cy="36" rx="12" ry="10" fill="#C0C0C0" />
    {/* Ojos de gato (rasgados) */}
    <ellipse cx="24" cy="28" rx="5" ry="6" fill="#90EE90" />
    <ellipse cx="40" cy="28" rx="5" ry="6" fill="#90EE90" />
    {/* Pupilas verticales de gato */}
    <ellipse cx="24" cy="28" rx="1.5" ry="5" fill="#1a1a1a" />
    <ellipse cx="40" cy="28" rx="1.5" ry="5" fill="#1a1a1a" />
    {/* Brillito en ojos */}
    <circle cx="25" cy="26" r="1.5" fill="white" />
    <circle cx="41" cy="26" r="1.5" fill="white" />
    {/* Nariz triangular de gato */}
    <polygon points="32,36 29,40 35,40" fill="#FFB6C1" />
    {/* Boca */}
    <path d="M32 40 L32 44" stroke="#666" strokeWidth="1.5" />
    <path d="M28 44 Q32 47 36 44" stroke="#666" fill="none" strokeWidth="1.5" strokeLinecap="round" />
    {/* Bigotes */}
    <line x1="10" y1="36" x2="22" y2="38" stroke="#666" strokeWidth="1" />
    <line x1="10" y1="40" x2="22" y2="40" stroke="#666" strokeWidth="1" />
    <line x1="10" y1="44" x2="22" y2="42" stroke="#666" strokeWidth="1" />
    <line x1="54" y1="36" x2="42" y2="38" stroke="#666" strokeWidth="1" />
    <line x1="54" y1="40" x2="42" y2="40" stroke="#666" strokeWidth="1" />
    <line x1="54" y1="44" x2="42" y2="42" stroke="#666" strokeWidth="1" />
  </svg>
);

const DogIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 80 80" className={className}>
    {/* Colita moviéndose arriba a la derecha */}
    <path d="M62 28 Q72 20 68 10" stroke="#CD853F" strokeWidth="5" fill="none" strokeLinecap="round" />

    {/* Cuerpo */}
    <ellipse cx="40" cy="55" rx="22" ry="16" fill="#CD853F" />
    {/* Panza clara */}
    <ellipse cx="40" cy="58" rx="14" ry="10" fill="#DEB887" />

    {/* Pata trasera izquierda */}
    <ellipse cx="24" cy="68" rx="6" ry="8" fill="#CD853F" />
    <ellipse cx="24" cy="74" rx="5" ry="4" fill="#8B4513" />
    {/* Pata trasera derecha */}
    <ellipse cx="56" cy="68" rx="6" ry="8" fill="#CD853F" />
    <ellipse cx="56" cy="74" rx="5" ry="4" fill="#8B4513" />

    {/* Pata delantera izquierda */}
    <ellipse cx="28" cy="62" rx="5" ry="10" fill="#CD853F" />
    <ellipse cx="28" cy="70" rx="4" ry="3" fill="#8B4513" />
    {/* Pata delantera derecha */}
    <ellipse cx="52" cy="62" rx="5" ry="10" fill="#CD853F" />
    <ellipse cx="52" cy="70" rx="4" ry="3" fill="#8B4513" />

    {/* Oreja izquierda (caída) */}
    <ellipse cx="22" cy="30" rx="7" ry="12" fill="#8B4513" />
    <ellipse cx="22" cy="30" rx="4" ry="8" fill="#A0522D" />
    {/* Oreja derecha (caída) */}
    <ellipse cx="58" cy="30" rx="7" ry="12" fill="#8B4513" />
    <ellipse cx="58" cy="30" rx="4" ry="8" fill="#A0522D" />

    {/* Cabeza */}
    <ellipse cx="40" cy="32" rx="18" ry="16" fill="#CD853F" />
    {/* Cara clara */}
    <ellipse cx="40" cy="36" rx="12" ry="11" fill="#DEB887" />

    {/* Hocico */}
    <ellipse cx="40" cy="42" rx="8" ry="5" fill="#DEB887" />

    {/* Ojos */}
    <ellipse cx="33" cy="30" rx="4" ry="5" fill="white" />
    <ellipse cx="47" cy="30" rx="4" ry="5" fill="white" />
    {/* Pupilas - mirando al frente feliz */}
    <circle cx="34" cy="31" r="2.5" fill="#2D1B0E" />
    <circle cx="48" cy="31" r="2.5" fill="#2D1B0E" />
    {/* Brillito en ojos */}
    <circle cx="35" cy="30" r="1" fill="white" />
    <circle cx="49" cy="30" r="1" fill="white" />

    {/* Cejas felices */}
    <path d="M29 24 Q33 22 37 24" stroke="#8B4513" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M43 24 Q47 22 51 24" stroke="#8B4513" strokeWidth="2" fill="none" strokeLinecap="round" />

    {/* Nariz */}
    <ellipse cx="40" cy="40" rx="4" ry="3" fill="#2D1B0E" />
    <ellipse cx="39" cy="39" rx="1.5" ry="1" fill="#4a3728" />

    {/* Boca sonriente */}
    <path d="M35 46 Q40 50 45 46" stroke="#2D1B0E" fill="none" strokeWidth="1.5" strokeLinecap="round" />
    {/* Lengua feliz */}
    <ellipse cx="40" cy="49" rx="3" ry="4" fill="#FF6B6B" />
    <path d="M40 47 L40 51" stroke="#E55555" strokeWidth="0.8" />
  </svg>
);

const PawPrint = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className} style={style}>
    <ellipse cx="16" cy="22" rx="7" ry="6" />
    <ellipse cx="7" cy="12" rx="4" ry="4.5" />
    <ellipse cx="13" cy="6" rx="3.5" ry="4" />
    <ellipse cx="19" cy="6" rx="3.5" ry="4" />
    <ellipse cx="25" cy="12" rx="4" ry="4.5" />
  </svg>
);

export default function Finances() {
  const now = new Date();
  const tenant = useAuthStore((s) => s.tenant);
  const [financeView, setFinanceView] = useState<'month' | 'day' | 'billing'>('month');
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<FixedExpense | null>(null);
  const [deleteExpenseId, setDeleteExpenseId] = useState<number | null>(null);

  // Modals para detalle del resumen anual
  const [showYearlyDetailModal, setShowYearlyDetailModal] = useState<'ventas' | 'costo' | 'gastos' | 'resultado' | null>(null);
  // Detalle de movimientos del mes (al clickear las tarjetas del resumen mensual)
  const [showMonthDetail, setShowMonthDetail] = useState<'ventas' | 'costo' | 'gastos' | 'resultado' | null>(null);
  // Filtro por rango de días dentro del modal de ventas
  const [salesFrom, setSalesFrom] = useState('');
  const [salesTo, setSalesTo] = useState('');
  useEffect(() => {
    // Al abrir/cambiar de modal, limpiar el rango
    setSalesFrom('');
    setSalesTo('');
  }, [showMonthDetail]);
  // Detalle de una categoria: se abre al clickear una tarjeta de categoria
  const [categoryDetail, setCategoryDetail] = useState<
    | { kind: 'expense'; label: string; categoryId?: number }
    | { kind: 'withdrawal'; label: string; category: string; recipient?: string | null }
    | null
  >(null);
  const [detailPeriod, setDetailPeriod] = useState<'month' | 'year' | 'all'>('month');
  // Cual de las dos descargas de "todos los gastos" esta en curso
  const [descargandoGastos, setDescargandoGastos] = useState<'mes' | 'anio' | null>(null);

  const closeCategoryDetail = () => {
    setCategoryDetail(null);
    setDetailPeriod('month');
  };

  // Form state
  const [formCategoryId, setFormCategoryId] = useState<string>('');
  const [formDescription, setFormDescription] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formIsRecurring, setFormIsRecurring] = useState(false);
  const [formNotes, setFormNotes] = useState('');

  // Queries
  const { data: categories = [] } = useExpenseCategories();
  const { data: expenses = [], isLoading: loadingExpenses } = useExpenses({
    month: selectedMonth,
    year: selectedYear,
  });
  const { data: summary, isLoading: loadingSummary } = useMonthlyFinancialSummary(
    selectedYear,
    selectedMonth
  );
  const { data: cashFlow } = useMonthlyCashFlow(selectedYear, selectedMonth);
  const { data: withdrawalBreakdown } = useWithdrawalBreakdown(selectedYear, selectedMonth);
  const { data: yearlySummary, isLoading: loadingYearly, error: yearlyError } = useYearlyFinancialSummary(selectedYear);

  // Queries para desgloses detallados (se activan cuando el modal está abierto)
  const { data: productCostBreakdown, isLoading: loadingProductCost } = useProductCostBreakdown(
    selectedYear,
    undefined,
    showYearlyDetailModal === 'costo'
  );
  const { data: salesBreakdown, isLoading: loadingSales } = useSalesBreakdown(
    selectedYear,
    undefined,
    showYearlyDetailModal === 'ventas'
  );

  // Detalle del MES (al hacer click en las tarjetas de arriba)
  const { data: monthSales, isLoading: loadingMonthSales } = useSalesBreakdown(
    selectedYear,
    selectedMonth,
    showMonthDetail === 'ventas'
  );
  const { data: monthCost, isLoading: loadingMonthCost } = useProductCostBreakdown(
    selectedYear,
    selectedMonth,
    showMonthDetail === 'costo'
  );

  // Ventas del mes filtradas por rango de días (cliente). Usa la fecha LOCAL de
  // cada venta (la misma que se muestra), y recalcula total y métodos de pago.
  const filteredMonthSales = useMemo(() => {
    const list = monthSales?.sales || [];
    const localYmd = (iso: string) => {
      const d = new Date(iso);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${mm}-${dd}`;
    };
    const rows = list.filter((s) => {
      const d = localYmd(s.date);
      return (!salesFrom || d >= salesFrom) && (!salesTo || d <= salesTo);
    });
    const byPm: Record<string, number> = { cash: 0, card: 0, transfer: 0 };
    let total = 0;
    for (const s of rows) {
      total += Number(s.total || 0);
      byPm[s.payment_method] = (byPm[s.payment_method] || 0) + Number(s.total || 0);
    }
    return { rows, total, byPm, count: rows.length };
  }, [monthSales, salesFrom, salesTo]);

  const salesRangeActive = Boolean(salesFrom || salesTo);

  // Detalle de una categoria de GASTOS FIJOS (el endpoint acepta category_id /
  // month / year opcionales, asi que sirve para mes, anio y todo).
  const expenseDetailFilters = useMemo(() => {
    const f: { category_id?: number; month?: number; year?: number } = {};
    if (categoryDetail?.kind === 'expense' && categoryDetail.categoryId) {
      f.category_id = categoryDetail.categoryId;
    }
    if (detailPeriod === 'month') {
      f.month = selectedMonth;
      f.year = selectedYear;
    } else if (detailPeriod === 'year') {
      f.year = selectedYear;
    }
    return f;
  }, [categoryDetail, detailPeriod, selectedMonth, selectedYear]);

  const { data: expenseDetailItems = [], isLoading: loadingExpenseDetail } = useExpenses(expenseDetailFilters);

  // Detalle de una categoria de SALIDAS DE CAJA
  const { data: withdrawalDetail, isLoading: loadingWithdrawalDetail } = useWithdrawalDetail(
    selectedYear,
    {
      month: detailPeriod === 'month' ? selectedMonth : undefined,
      category: categoryDetail?.kind === 'withdrawal' ? categoryDetail.category : undefined,
      recipient:
        categoryDetail?.kind === 'withdrawal' && categoryDetail.recipient
          ? categoryDetail.recipient
          : undefined,
    },
    categoryDetail?.kind === 'withdrawal'
  );

  const expenseDetailTotal = useMemo(
    () => expenseDetailItems.reduce((acc, e) => acc + Number(e.amount || 0), 0),
    [expenseDetailItems]
  );

  // Consolidado de SUELDOS del mes: junta el circuito de gasto fijo ("Sueldos")
  // con el de salidas de caja (category === 'salary'), que hoy se ven separados.
  const salaryConsolidated = useMemo(() => {
    const parts: { label: string; total: number; source: 'fijo' | 'caja' }[] = [];

    for (const c of summary?.expenses_by_category || []) {
      if (c.category.toLowerCase().includes('sueldo')) {
        parts.push({ label: `Gasto fijo · ${c.category}`, total: Number(c.total || 0), source: 'fijo' });
      }
    }
    for (const c of withdrawalBreakdown || []) {
      if (c.category === 'salary') {
        parts.push({ label: `Caja · ${c.label}`, total: Number(c.total || 0), source: 'caja' });
      }
    }

    const totalFijo = parts.filter((p) => p.source === 'fijo').reduce((a, p) => a + p.total, 0);
    const totalCaja = parts.filter((p) => p.source === 'caja').reduce((a, p) => a + p.total, 0);
    return { parts, totalFijo, totalCaja, total: totalFijo + totalCaja };
  }, [summary, withdrawalBreakdown]);

  // Agrupado por mes (cuando el periodo abarca varios meses)
  const expenseDetailByMonth = useMemo(() => {
    const map = new Map<string, { year: number; month: number; total: number; count: number }>();
    for (const e of expenseDetailItems) {
      const key = `${e.year}-${e.month}`;
      const prev = map.get(key) || { year: e.year, month: e.month, total: 0, count: 0 };
      prev.total += Number(e.amount || 0);
      prev.count += 1;
      map.set(key, prev);
    }
    return Array.from(map.values()).sort((a, b) => b.year - a.year || b.month - a.month);
  }, [expenseDetailItems]);

  // --- Descargas en CSV -----------------------------------------------------
  // Siempre bajan lo que esta mostrando la pantalla en ese momento: si hay un
  // rango de dias o un periodo elegido, el archivo sale con ese recorte.

  /** Sufijo con el periodo, para que el archivo se distinga de otras descargas. */
  const periodoArchivo = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

  const descargarVentas = () => {
    const filas = filteredMonthSales.rows.map((s) => [
      fechaCsv(s.date),
      s.owner_name || '',
      s.items_count,
      paymentMethodLabel(s.payment_method),
      numeroCsv(s.total),
    ]);
    const rango = salesRangeActive ? `_${salesFrom || 'inicio'}_a_${salesTo || 'fin'}` : '';
    descargarCsv(
      `ventas_${periodoArchivo}${rango}`,
      ['Fecha', 'Cliente', 'Items', 'Pago', 'Total'],
      filas,
    );
  };

  const filasDeGastos = (items: FixedExpense[]) =>
    items.map((e) => [
      `${MONTH_NAMES[e.month - 1]} ${e.year}`,
      e.category?.name || '',
      e.description || '',
      e.is_paid ? 'Pagado' : 'Pendiente',
      fechaCsv(e.paid_at),
      numeroCsv(e.amount),
    ]);

  const ENCABEZADOS_GASTOS = ['Periodo', 'Categoria', 'Descripcion', 'Estado', 'Fecha de pago', 'Monto'];

  const descargarGastosDelMes = () => {
    descargarCsv(`gastos_fijos_${periodoArchivo}`, ENCABEZADOS_GASTOS, filasDeGastos(expenses));
  };

  const descargarGastosDeCategoria = () => {
    // El nombre lleva la categoria y el periodo elegido en los chips
    const cat = (categoryDetail?.label || 'categoria')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    const periodo =
      detailPeriod === 'month' ? periodoArchivo : detailPeriod === 'year' ? String(selectedYear) : 'todo';
    descargarCsv(`gastos_${cat}_${periodo}`, ENCABEZADOS_GASTOS, filasDeGastos(expenseDetailItems));
  };

  /**
   * Todos los gastos del periodo en un solo archivo.
   *
   * Los gastos de la veterinaria salen por dos circuitos distintos: los gastos fijos
   * que se cargan a mano y las salidas de caja del dia. Para cerrar el mes hace falta
   * verlos juntos, asi que el archivo trae los dos con una columna que dice de donde
   * viene cada fila. Se piden al servidor en el momento de la descarga: el periodo
   * puede ser mas amplio que el que esta cargado en pantalla.
   */
  const descargarTodosLosGastos = async (alcance: 'mes' | 'anio') => {
    setDescargandoGastos(alcance);
    try {
      const mes = alcance === 'mes' ? selectedMonth : undefined;
      const [fijos, salidas] = await Promise.all([
        financeService.getExpenses({ year: selectedYear, month: mes }),
        financeService.getWithdrawalDetail(selectedYear, { month: mes }),
      ]);

      const periodoDe = (iso?: string | null, mesDato?: number, anioDato?: number) => {
        if (mesDato && anioDato) return `${MONTH_NAMES[mesDato - 1]} ${anioDato}`;
        if (!iso) return '';
        const d = new Date(iso);
        return isNaN(d.getTime()) ? '' : `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
      };

      const filas = [
        ...fijos.map((e) => ({
          orden: e.paid_at || `${e.year}-${String(e.month).padStart(2, '0')}-01`,
          celdas: [
            'Gasto fijo',
            fechaCsv(e.paid_at),
            periodoDe(null, e.month, e.year),
            e.category?.name || '',
            e.description || '',
            '',
            '',
            e.is_paid ? 'Pagado' : 'Pendiente',
            numeroCsv(e.amount),
          ],
        })),
        ...salidas.items.map((it) => ({
          orden: it.date || '',
          celdas: [
            'Salida de caja',
            fechaCsv(it.date),
            periodoDe(it.date),
            it.label,
            it.description || '',
            it.recipient || '',
            it.payment_method ? paymentMethodLabel(it.payment_method) : '',
            '',
            numeroCsv(it.amount),
          ],
        })),
      ]
        .sort((a, b) => a.orden.localeCompare(b.orden))
        .map((f) => f.celdas);

      descargarCsv(
        `gastos_${alcance === 'mes' ? periodoArchivo : selectedYear}`,
        ['Circuito', 'Fecha', 'Periodo', 'Categoria', 'Descripcion', 'Destinatario', 'Pago', 'Estado', 'Monto'],
        filas,
      );
    } finally {
      setDescargandoGastos(null);
    }
  };

  const descargarSalidasDeCaja = () => {
    if (!withdrawalDetail) return;
    const filas = withdrawalDetail.items.map((it) => [
      fechaCsv(it.date),
      it.label,
      it.description || '',
      it.recipient || '',
      it.payment_method ? paymentMethodLabel(it.payment_method) : '',
      numeroCsv(it.amount),
    ]);
    const periodo = detailPeriod === 'month' ? periodoArchivo : String(selectedYear);
    descargarCsv(
      `salidas_de_caja_${periodo}`,
      ['Fecha', 'Categoria', 'Descripcion', 'Destinatario', 'Pago', 'Monto'],
      filas,
    );
  };

  // Debug: mostrar en consola si hay error
  if (yearlyError) {
    console.error('Error cargando resumen anual:', yearlyError);
  }

  // Servicios: arranca en el día de hoy y se puede abrir a un rango de fechas
  const hoyISO = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${mm}-${dd}`;
  };
  const [serviceFrom, setServiceFrom] = useState(hoyISO);
  const [serviceTo, setServiceTo] = useState(hoyISO);
  const { data: serviceBreakdown, isLoading: loadingServices } = useServiceSalesBreakdown(
    serviceFrom,
    serviceTo
  );

  // Mutations
  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const deleteExpense = useDeleteExpense();
  const togglePaid = useToggleExpensePaid();
  const copyRecurring = useCopyRecurringExpenses();

  // OJO: acá había un auto-copiado de gastos recurrentes que se disparaba solo cuando el
  // mes quedaba en cero. Eso hacía que al borrar los gastos de un mes para cargar lo que
  // se pagó de verdad, "volvieran" solos (y también al recargar la página o entrar desde
  // otra compu). Ahora se copian únicamente con el botón "Copiar Recurrentes".

  // Navigation
  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const goToCurrentMonth = () => {
    setSelectedMonth(now.getMonth() + 1);
    setSelectedYear(now.getFullYear());
  };

  // Form handlers
  const resetForm = () => {
    setFormCategoryId('');
    setFormDescription('');
    setFormAmount('');
    setFormIsRecurring(false);
    setFormNotes('');
    setEditingExpense(null);
  };

  const openNewExpenseModal = () => {
    resetForm();
    setShowExpenseModal(true);
  };

  const openEditExpenseModal = (expense: FixedExpense) => {
    setEditingExpense(expense);
    setFormCategoryId(expense.category_id?.toString() || '');
    setFormDescription(expense.description || '');
    setFormAmount(formatAmountForDisplay(Number(expense.amount)));
    setFormIsRecurring(expense.is_recurring);
    setFormNotes(expense.notes || '');
    setShowExpenseModal(true);
  };

  // Helper functions para formateo de montos
  // Recibe un NUMERO y lo muestra como "1.234,56".
  // Antes recibia un string y le sacaba los puntos antes de parsear. Eso sirve para un
  // texto ya formateado ("1.234,56"), pero los dos lugares que la llaman le pasan un
  // valor canonico ("22000.00"), donde el punto es el separador DECIMAL: al quitarlo,
  // 22.000 se convertia en 2.200.000. Por eso al abrir un gasto el monto se multiplicaba
  // por 100, y si lo guardaban asi quedaba mal en la base.
  const formatAmountForDisplay = (value: number): string => {
    if (!isFinite(value)) return '';
    return value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const parseAmountFromDisplay = (value: string): string => {
    // Convierte "1.234,56" a "1234.56" para enviar al backend
    return value.replace(/\./g, '').replace(',', '.');
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Solo permitir números, puntos y comas
    value = value.replace(/[^0-9.,]/g, '');
    setFormAmount(value);
  };

  const handleAmountBlur = () => {
    // Formatear al salir del campo
    if (formAmount) {
      const parsed = parseFloat(parseAmountFromDisplay(formAmount));
      if (!isNaN(parsed)) {
        setFormAmount(formatAmountForDisplay(parsed));
      }
    }
  };

  const handleSaveExpense = async () => {
    // Solo requerir monto (descripción es opcional)
    if (!formAmount) return;

    const parsedAmount = parseFloat(parseAmountFromDisplay(formAmount));
    if (isNaN(parsedAmount) || parsedAmount <= 0) return;

    const data: FixedExpenseCreate = {
      category_id: formCategoryId ? parseInt(formCategoryId) : undefined,
      description: formDescription || undefined,
      amount: parsedAmount,
      month: selectedMonth,
      year: selectedYear,
      is_recurring: formIsRecurring,
      notes: formNotes || undefined,
    };

    try {
      if (editingExpense) {
        await updateExpense.mutateAsync({
          id: editingExpense.id,
          data,
        });
      } else {
        await createExpense.mutateAsync(data);
      }
      setShowExpenseModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving expense:', error);
    }
  };

  const handleDeleteExpense = async () => {
    if (!deleteExpenseId) return;
    try {
      await deleteExpense.mutateAsync(deleteExpenseId);
      setShowDeleteModal(false);
      setDeleteExpenseId(null);
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const handleTogglePaid = async (id: number) => {
    try {
      await togglePaid.mutateAsync(id);
    } catch (error) {
      console.error('Error toggling paid:', error);
    }
  };

  const handleCopyRecurring = async () => {
    // Copiar del mes anterior al mes actual
    let sourceMonth = selectedMonth - 1;
    let sourceYear = selectedYear;
    if (sourceMonth === 0) {
      sourceMonth = 12;
      sourceYear = selectedYear - 1;
    }

    try {
      await copyRecurring.mutateAsync({
        source_month: sourceMonth,
        source_year: sourceYear,
        target_month: selectedMonth,
        target_year: selectedYear,
      });
      setShowCopyModal(false);
    } catch (error) {
      console.error('Error copying expenses:', error);
    }
  };

  // Group expenses by category for display
  const expensesByCategory = useMemo(() => {
    const grouped: Record<string, FixedExpense[]> = {};
    expenses.forEach((expense) => {
      const catName = expense.category?.name || 'Sin categoría';
      if (!grouped[catName]) {
        grouped[catName] = [];
      }
      grouped[catName].push(expense);
    });
    return grouped;
  }, [expenses]);

  // Determine result status
  const netResult = summary?.net_result || 0;
  const isPositive = netResult >= 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finanzas</h1>
          <p className="text-gray-600">Control de gastos fijos y rentabilidad mensual</p>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={goToPreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-4 py-2 bg-white rounded-lg border text-center min-w-[160px]">
            <span className="font-medium">
              {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
            </span>
          </div>
          <Button variant="secondary" size="sm" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="secondary" size="sm" onClick={goToCurrentMonth} title="Ir al mes actual">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs: Mes / Día */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          type="button"
          onClick={() => setFinanceView('month')}
          className={clsx(
            'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
            financeView === 'month' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Mes
        </button>
        <button
          type="button"
          onClick={() => setFinanceView('day')}
          className={clsx(
            'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
            financeView === 'day' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Día (por categoría)
        </button>
        {/* La facturación electrónica no la tienen todas las veterinarias */}
        {tenant?.has_billing_arca && (
          <button
            type="button"
            onClick={() => setFinanceView('billing')}
            className={clsx(
              'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
              financeView === 'billing' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Facturado (ARCA)
          </button>
        )}
      </div>

      {financeView === 'day' && <DailyCategorySummary />}

      {financeView === 'billing' && <BillingSummary />}

      {financeView === 'month' && (
      <>
      {/*
        Bajar todos los gastos de una. Las tarjetas de cada categoria tienen su
        propia descarga, pero para cerrar el mes con la contadora hace falta el
        listado completo, y en un solo archivo con los dos circuitos juntos.
      */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-500">Descargar todos los gastos:</span>
        <button
          type="button"
          onClick={() => descargarTodosLosGastos('mes')}
          disabled={descargandoGastos !== null}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
          {descargandoGastos === 'mes' ? 'Preparando...' : `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`}
        </button>
        <button
          type="button"
          onClick={() => descargarTodosLosGastos('anio')}
          disabled={descargandoGastos !== null}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
          {descargandoGastos === 'anio' ? 'Preparando...' : `Todo ${selectedYear}`}
        </button>
      </div>

      {/* Summary Cards */}
      {loadingSummary ? (
        <div className="text-center py-4">Cargando resumen...</div>
      ) : summary ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Ingresos (Ventas) */}
          <button
            type="button"
            onClick={() => setShowMonthDetail('ventas')}
            title="Ver las ventas que componen este total"
            className="bg-white rounded-xl shadow-sm border p-5 text-left w-full hover:border-blue-300 hover:shadow-md transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ventas del Mes</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatPrice(summary.total_income)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </button>

          {/* Gastos Variables */}
          <button
            type="button"
            onClick={() => setShowMonthDetail('costo')}
            title="Ver el detalle de gastos variables"
            className="bg-white rounded-xl shadow-sm border p-5 text-left w-full hover:border-orange-300 hover:shadow-md transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gastos Variables</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatPrice(summary.total_variable_expenses)}
                </p>
                {summary.total_cost > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Costo prod: {formatPrice(summary.total_cost)}
                  </p>
                )}
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <TrendingDown className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </button>

          {/* Gastos Fijos */}
          <button
            type="button"
            onClick={() => setShowMonthDetail('gastos')}
            title="Ver los gastos fijos del mes"
            className="bg-white rounded-xl shadow-sm border p-5 text-left w-full hover:border-red-300 hover:shadow-md transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gastos Fijos</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatPrice(summary.total_fixed_expenses)}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <span className="text-green-600">
                    Pagado: {formatPrice(summary.fixed_expenses_paid)}
                  </span>
                  {summary.fixed_expenses_pending > 0 && (
                    <span className="text-orange-600">
                      Pendiente: {formatPrice(summary.fixed_expenses_pending)}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </button>

          {/* Resultado Neto */}
          <button
            type="button"
            onClick={() => setShowMonthDetail('resultado')}
            title="Ver cómo se compone el resultado del mes"
            className={clsx(
              'rounded-xl shadow-sm border p-5 text-left w-full hover:shadow-md transition-all',
              isPositive ? 'bg-green-50 border-green-200 hover:border-green-300' : 'bg-red-50 border-red-200 hover:border-red-300'
            )}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={clsx('text-sm', isPositive ? 'text-green-700' : 'text-red-700')}>
                  Resultado Neto
                </p>
                <p
                  className={clsx(
                    'text-2xl font-bold mt-1',
                    isPositive ? 'text-green-700' : 'text-red-700'
                  )}
                >
                  {formatPrice(netResult)}
                </p>
                <p className={clsx('text-xs mt-1', isPositive ? 'text-green-600' : 'text-red-600')}>
                  {isPositive ? 'Ganancia' : 'Pérdida'} del mes
                </p>
              </div>
              <div className={clsx('p-3 rounded-lg', isPositive ? 'bg-green-200' : 'bg-red-200')}>
                <PiggyBank className={clsx('h-6 w-6', isPositive ? 'text-green-700' : 'text-red-700')} />
              </div>
            </div>
          </button>
        </div>
      ) : null}

      {/* Flujo de Caja */}
      {cashFlow ? (
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Flujo de Caja</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600">Cobros (ventas)</span>
              <span className="text-sm font-semibold text-green-700">+{formatPrice(cashFlow.total_income)}</span>
            </div>
            {cashFlow.supplier_payments > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">Pagos a proveedores</span>
                <span className="text-sm font-semibold text-red-600">-{formatPrice(cashFlow.supplier_payments)}</span>
              </div>
            ) : null}
            {cashFlow.fixed_expenses_paid > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">Gastos fijos pagados</span>
                <span className="text-sm font-semibold text-red-600">-{formatPrice(cashFlow.fixed_expenses_paid)}</span>
              </div>
            ) : null}
            {cashFlow.operational_expenses > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">Gastos operativos</span>
                <span className="text-sm font-semibold text-red-600">-{formatPrice(cashFlow.operational_expenses)}</span>
              </div>
            ) : null}
            {cashFlow.salary_expenses > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">Sueldos</span>
                <span className="text-sm font-semibold text-red-600">-{formatPrice(cashFlow.salary_expenses)}</span>
              </div>
            ) : null}
            {(cashFlow.merchandise_purchases ?? 0) > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">
                  Mercadería e insumos
                  <span className="block text-xs text-gray-400">Sale plata, pero no es gasto: el costo llega al vender</span>
                </span>
                <span className="text-sm font-semibold text-orange-600">-{formatPrice(cashFlow.merchandise_purchases ?? 0)}</span>
              </div>
            ) : null}
            <div className="flex justify-between items-center py-3 border-t-2 border-gray-200">
              <span className="text-sm font-bold text-gray-900">Resultado operativo</span>
              <span className={clsx(
                'text-base font-bold',
                cashFlow.operating_cash_flow >= 0 ? 'text-green-700' : 'text-red-700'
              )}>
                {formatPrice(cashFlow.operating_cash_flow)}
              </span>
            </div>
            {cashFlow.withdrawals > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-400">Retiros personales</span>
                <span className="text-sm font-medium text-gray-400">-{formatPrice(cashFlow.withdrawals)}</span>
              </div>
            ) : null}
            <div className="flex justify-between items-center py-3 border-t border-dashed border-gray-300">
              <span className="text-sm font-bold text-gray-500">Flujo neto</span>
              <span className={clsx(
                'text-base font-bold',
                cashFlow.net_cash_flow >= 0 ? 'text-green-600' : 'text-gray-500'
              )}>
                {formatPrice(cashFlow.net_cash_flow)}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {/* Total por servicio del día (para el cierre diario) */}
      <div className="bg-white rounded-xl shadow-sm border border-teal-200 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-teal-600" />
              Servicios
            </h3>
            <p className="text-sm text-gray-500">
              Qué servicios se cobraron, cuántas veces y cuánto sumó cada uno.
            </p>
            {/* Atajos: el cierre del día es lo mas usado, pero tambien sirve el mes */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <button
                type="button"
                onClick={() => { setServiceFrom(hoyISO()); setServiceTo(hoyISO()); }}
                className="text-xs px-2 py-1 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 font-medium"
              >
                Hoy
              </button>
              <button
                type="button"
                onClick={() => {
                  const d = new Date();
                  d.setDate(d.getDate() - 6);
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  setServiceFrom(`${d.getFullYear()}-${mm}-${dd}`);
                  setServiceTo(hoyISO());
                }}
                className="text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium"
              >
                Últimos 7 días
              </button>
              <button
                type="button"
                onClick={() => {
                  const mm = String(selectedMonth).padStart(2, '0');
                  const ultimo = new Date(selectedYear, selectedMonth, 0).getDate();
                  setServiceFrom(`${selectedYear}-${mm}-01`);
                  setServiceTo(`${selectedYear}-${mm}-${String(ultimo).padStart(2, '0')}`);
                }}
                className="text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium"
              >
                {MONTH_NAMES[selectedMonth - 1]}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">Desde</span>
              <input
                type="date"
                value={serviceFrom}
                onChange={(e) => setServiceFrom(e.target.value)}
                className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <span className="text-xs text-gray-500">hasta</span>
              <input
                type="date"
                value={serviceTo}
                onChange={(e) => setServiceTo(e.target.value)}
                className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-teal-700 tabular-nums leading-tight">
                {formatPrice(serviceBreakdown?.total ?? 0)}
              </p>
              <p className="text-xs text-gray-400">
                {serviceBreakdown?.items_count ?? 0} cobrado{(serviceBreakdown?.items_count ?? 0) === 1 ? '' : 's'}
              </p>
            </div>
          </div>
        </div>

        {loadingServices ? (
          <p className="text-sm text-gray-400 py-4 text-center">Cargando servicios...</p>
        ) : !serviceBreakdown || serviceBreakdown.services.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">
            No se cobraron servicios en ese período.
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            {serviceBreakdown.services.map((s) => (
              <div key={s.product_id ?? s.name} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                  <p className="text-xs text-gray-500">
                    {s.quantity} {s.quantity === 1 ? 'vez' : 'veces'}
                  </p>
                </div>
                <span className="text-base font-bold text-teal-700 tabular-nums">{formatPrice(s.total)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Consolidado de Sueldos (gasto fijo + salidas de caja) */}
      {salaryConsolidated.total > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-indigo-200 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-600" />
                Sueldos del mes (consolidado)
              </h3>
              <p className="text-sm text-gray-500">
                Suma lo cargado como <strong>gasto fijo</strong> más las <strong>salidas de caja</strong> de sueldos.
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-indigo-700 tabular-nums leading-tight">
                {formatPrice(salaryConsolidated.total)}
              </p>
              <p className="text-xs text-gray-400">
                {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </p>
            </div>
          </div>

          {/* Los dos circuitos */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
              <p className="text-xs text-gray-500">Gastos fijos</p>
              <p className="text-base font-bold text-gray-900 tabular-nums">{formatPrice(salaryConsolidated.totalFijo)}</p>
            </div>
            <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2">
              <p className="text-xs text-indigo-600">Salidas de caja</p>
              <p className="text-base font-bold text-indigo-800 tabular-nums">{formatPrice(salaryConsolidated.totalCaja)}</p>
            </div>
          </div>

          {/* Desglose de cada origen */}
          <div className="space-y-1">
            {salaryConsolidated.parts.map((p, idx) => (
              <div key={`${p.label}-${idx}`} className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600 break-words min-w-0">{p.label}</span>
                <span className="font-medium text-gray-900 whitespace-nowrap tabular-nums">{formatPrice(p.total)}</span>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Nota: en "Gastos por Categoría" y "Salidas de Caja" cada circuito se muestra por separado; acá van sumados.
          </p>
        </div>
      )}

      {/* Expenses by Category Summary */}
      {summary && summary.expenses_by_category.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Gastos por Categoría</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {summary.expenses_by_category.map((cat) => (
              <button
                key={cat.category}
                type="button"
                onClick={() =>
                  setCategoryDetail({
                    kind: 'expense',
                    label: cat.category,
                    categoryId: categories.find((c) => c.name === cat.category)?.id,
                  })
                }
                title={`Ver el detalle de ${cat.category}`}
                className="group p-3 rounded-lg bg-gray-50 border border-gray-100 text-left hover:bg-white hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <p className="text-sm font-medium text-gray-700 break-words">{cat.category}</p>
                <p className="text-lg font-bold text-gray-900 tabular-nums">{formatPrice(cat.total)}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  {cat.count} gasto(s)
                  <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Desglose de Salidas de Caja */}
      {withdrawalBreakdown && withdrawalBreakdown.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-baseline justify-between gap-2 mb-4">
            <h3 className="font-semibold text-gray-900">Salidas de Caja por Categoría</h3>
            <span className="text-xs text-gray-400">Tocá una categoría para ver el detalle</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {withdrawalBreakdown.map((cat, idx) => (
              <button
                key={`${cat.category}-${cat.label}-${idx}`}
                type="button"
                onClick={() =>
                  setCategoryDetail({
                    kind: 'withdrawal',
                    label: cat.label,
                    category: cat.category,
                    recipient: cat.recipient ?? null,
                  })
                }
                title={`Ver los movimientos de ${cat.label}`}
                className={clsx(
                  'group p-3 rounded-lg border text-left transition-all hover:shadow-sm',
                  cat.category === 'salary' ? 'bg-indigo-50 border-indigo-200 hover:border-indigo-400' :
                  cat.category === 'withdrawal' ? 'bg-blue-50 border-blue-200 hover:border-blue-400' :
                  cat.category === 'supplier_payment' ? 'bg-amber-50 border-amber-200 hover:border-amber-400' :
                  'bg-gray-50 border-gray-100 hover:border-gray-300'
                )}
              >
                <p className="text-sm font-medium text-gray-700 break-words">{cat.label}</p>
                <p className="text-lg font-bold text-gray-900 tabular-nums">{formatPrice(cat.total)}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  {cat.count} movimiento(s)
                  <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={openNewExpenseModal}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Gasto
        </Button>
        <Button variant="secondary" onClick={() => setShowCopyModal(true)}>
          <Copy className="h-4 w-4 mr-2" />
          Copiar Recurrentes
        </Button>
      </div>

      {/* Expenses List */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-5 border-b">
          <h3 className="font-semibold text-gray-900">
            Gastos de {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
          </h3>
        </div>

        {loadingExpenses ? (
          <div className="text-center py-8">Cargando gastos...</div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No hay gastos registrados para este mes</p>
            <p className="text-sm text-gray-400 mt-1">
              Podés cargarlos a mano o traer los recurrentes del mes anterior.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              <Button variant="secondary" onClick={openNewExpenseModal}>
                <Plus className="h-4 w-4 mr-2" />
                Agregar primer gasto
              </Button>
              <Button variant="secondary" onClick={() => setShowCopyModal(true)}>
                <Copy className="h-4 w-4 mr-2" />
                Copiar Recurrentes
              </Button>
            </div>
          </div>
        ) : (
          <div className="divide-y">
            {Object.entries(expensesByCategory).map(([categoryName, categoryExpenses]) => {
              const firstExpense = categoryExpenses[0];
              const categoryIcon = firstExpense?.category?.icon;
              const IconComponent = categoryIcon ? CATEGORY_ICONS[categoryIcon] : MoreHorizontal;
              const categoryColor = firstExpense?.category?.color || 'gray';

              return (
                <div key={categoryName} className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className={clsx(
                        'p-1.5 rounded-lg',
                        CATEGORY_COLORS[categoryColor] || 'bg-gray-100 text-gray-800'
                      )}
                    >
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <h4 className="font-medium text-gray-700">{categoryName}</h4>
                    <span className="text-sm text-gray-500">
                      ({categoryExpenses.length})
                    </span>
                  </div>

                  <div className="space-y-2 ml-8">
                    {categoryExpenses.map((expense) => (
                      <div
                        key={expense.id}
                        className={clsx(
                          'flex items-center justify-between p-3 rounded-lg border',
                          expense.is_paid
                            ? 'bg-green-50 border-green-100'
                            : 'bg-gray-50 border-gray-100'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleTogglePaid(expense.id)}
                            className={clsx(
                              'p-1 rounded-full transition-colors',
                              expense.is_paid
                                ? 'text-green-600 hover:bg-green-100'
                                : 'text-gray-400 hover:bg-gray-200'
                            )}
                            title={expense.is_paid ? 'Marcar como pendiente' : 'Marcar como pagado'}
                          >
                            {expense.is_paid ? (
                              <CheckCircle2 className="h-5 w-5" />
                            ) : (
                              <Clock className="h-5 w-5" />
                            )}
                          </button>

                          <div>
                            <p
                              className={clsx(
                                'font-medium',
                                expense.is_paid ? 'text-green-800' : 'text-gray-900'
                              )}
                            >
                              {expense.description || expense.category?.name || 'Sin descripción'}
                              {expense.is_recurring && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                  Recurrente
                                </span>
                              )}
                            </p>
                            {expense.notes && (
                              <p className="text-sm text-gray-500">{expense.notes}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <span
                            className={clsx(
                              'font-bold',
                              expense.is_paid ? 'text-green-700' : 'text-gray-900'
                            )}
                          >
                            {formatPrice(expense.amount)}
                          </span>

                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEditExpenseModal(expense)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Editar"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setDeleteExpenseId(expense.id);
                                setShowDeleteModal(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Resumen Anual Acumulado */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg">
            <PiggyBank className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Acumulado Anual {selectedYear}</h3>
            <p className="text-sm text-gray-500">Resumen de todo el año hasta el mes actual</p>
          </div>
        </div>

        {/* Estado de carga */}
        {loadingYearly && (
          <div className="text-center py-8 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mb-2"></div>
            Cargando resumen anual...
          </div>
        )}

        {/* Estado de error */}
        {yearlyError && !loadingYearly && (
          <div className="text-center py-8 text-red-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            Error al cargar el resumen anual
            <p className="text-xs text-gray-400 mt-1">{String(yearlyError)}</p>
          </div>
        )}

        {/* Datos cargados */}
        {yearlySummary && !loadingYearly && (
          <>
            {/* Barra visual con animalitos */}
            <div className="relative mt-6 mb-4">
              {/* Fondo con huellas */}
              <div className="absolute inset-0 flex items-center justify-around opacity-10 pointer-events-none">
                <PawPrint className="w-6 h-6 text-gray-600" style={{ transform: 'rotate(-15deg)' }} />
                <PawPrint className="w-5 h-5 text-gray-600" style={{ transform: 'rotate(10deg)' }} />
                <PawPrint className="w-6 h-6 text-gray-600" style={{ transform: 'rotate(-5deg)' }} />
                <PawPrint className="w-5 h-5 text-gray-600" style={{ transform: 'rotate(20deg)' }} />
              </div>

              {/* Barra de progreso */}
              <div className="relative h-12 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full overflow-hidden border-2 border-gray-300">
                {(() => {
                  const profit = yearlySummary.total_profit;
                  const expenses = yearlySummary.total_expenses;
                  const total = profit + expenses;
                  const profitPercent = total > 0 ? (profit / total) * 100 : 50;
                  const isPositive = yearlySummary.net_result >= 0;

                return (
                  <>
                    {/* Lado de ganancias (verde) */}
                    <div
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-1000 ease-out"
                      style={{ width: `${profitPercent}%` }}
                    >
                      <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.1)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.1)_50%,rgba(255,255,255,0.1)_75%,transparent_75%)] bg-[length:20px_20px] animate-[stripe_1s_linear_infinite]" />
                    </div>

                    {/* Lado de gastos (rojo) */}
                    <div
                      className="absolute right-0 top-0 h-full bg-gradient-to-l from-red-400 to-red-500 transition-all duration-1000 ease-out"
                      style={{ width: `${100 - profitPercent}%` }}
                    >
                      <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.1)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.1)_50%,rgba(255,255,255,0.1)_75%,transparent_75%)] bg-[length:20px_20px] animate-[stripe_1s_linear_infinite]" />
                    </div>

                    {/* Perrito en la posición de balance */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 transition-all duration-1000 ease-out z-10"
                      style={{
                        left: `${profitPercent}%`,
                        transform: `translateX(-50%) translateY(-50%)`
                      }}
                    >
                      <div className={clsx(
                        'w-14 h-14 rounded-full flex items-center justify-center shadow-lg border-4 transition-all',
                        isPositive
                          ? 'bg-green-100 border-green-500'
                          : 'bg-red-100 border-red-500'
                      )}>
                        {isPositive ? (
                          <DogIcon className="w-9 h-9 text-green-600" />
                        ) : (
                          <CatIcon className="w-9 h-9 text-red-600" />
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Labels de la barra */}
            <div className="flex justify-between mt-2">
              <div className="flex items-center gap-2">
                <DogIcon className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-700">Ganancias</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-red-700">Gastos</span>
                <CatIcon className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </div>

          {/* Números del resumen anual - clickeables */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <button
              onClick={() => setShowYearlyDetailModal('ventas')}
              className="text-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer border border-transparent hover:border-blue-200"
            >
              <p className="text-xs text-blue-600 uppercase tracking-wide">Ventas</p>
              <p className="text-lg font-bold text-blue-700">{formatPrice(yearlySummary.total_income)}</p>
            </button>
            <button
              onClick={() => setShowYearlyDetailModal('costo')}
              className="text-center p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors cursor-pointer border border-transparent hover:border-orange-200"
            >
              <p className="text-xs text-orange-600 uppercase tracking-wide">Gastos Variables</p>
              <p className="text-lg font-bold text-orange-700">{formatPrice(yearlySummary.total_variable_expenses)}</p>
            </button>
            <button
              onClick={() => setShowYearlyDetailModal('gastos')}
              className="text-center p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer border border-transparent hover:border-red-200"
            >
              <p className="text-xs text-red-600 uppercase tracking-wide">Gastos Fijos</p>
              <p className="text-lg font-bold text-red-700">{formatPrice(yearlySummary.total_expenses)}</p>
            </button>
            <button
              onClick={() => setShowYearlyDetailModal('resultado')}
              className={clsx(
                'text-center p-3 rounded-lg transition-colors cursor-pointer border border-transparent',
                yearlySummary.net_result >= 0
                  ? 'bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-200'
                  : 'bg-rose-50 hover:bg-rose-100 hover:border-rose-200'
              )}
            >
              <p className={clsx(
                'text-xs uppercase tracking-wide',
                yearlySummary.net_result >= 0 ? 'text-emerald-600' : 'text-rose-600'
              )}>
                Resultado Neto
              </p>
              <p className={clsx(
                'text-lg font-bold',
                yearlySummary.net_result >= 0 ? 'text-emerald-700' : 'text-rose-700'
              )}>
                {formatPrice(yearlySummary.net_result)}
              </p>
            </button>
          </div>

          {/* Mini leyenda con animalitos */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-6 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <DogIcon className="w-4 h-4 text-green-500" />
              <span>= Más ganancias que gastos</span>
            </div>
            <div className="flex items-center gap-1">
              <CatIcon className="w-4 h-4 text-red-500" />
              <span>= Más gastos que ganancias</span>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Create/Edit Expense Modal */}
      <Modal
        isOpen={showExpenseModal}
        onClose={() => {
          setShowExpenseModal(false);
          resetForm();
        }}
        title={editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
      >
        <div className="space-y-4">
          <Select
            label="Categoría"
            value={formCategoryId}
            onChange={(e) => setFormCategoryId(e.target.value)}
            options={[
              { value: '', label: 'Sin categoría' },
              ...categories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
          />

          <Input
            label="Descripción (opcional)"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="Ej: Alquiler local"
          />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Monto <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={formAmount}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                placeholder="0,00"
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
              />
            </div>
          </div>

          <Input
            label="Notas (opcional)"
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
            placeholder="Notas adicionales..."
          />

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formIsRecurring}
              onChange={(e) => setFormIsRecurring(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              Gasto recurrente (se puede copiar al siguiente mes)
            </span>
          </label>

          <div className="flex gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowExpenseModal(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveExpense}
              disabled={!formAmount || createExpense.isPending || updateExpense.isPending}
              className="flex-1"
            >
              {createExpense.isPending || updateExpense.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteExpenseId(null);
        }}
        title="Eliminar Gasto"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Estás seguro de que deseas eliminar este gasto? Esta acción no se puede deshacer.
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteExpenseId(null);
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteExpense}
              disabled={deleteExpense.isPending}
              className="flex-1"
            >
              {deleteExpense.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Copy Recurring Modal */}
      <Modal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        title="Copiar Gastos Recurrentes"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Esta acción copiará todos los gastos marcados como "recurrentes" del mes anterior a{' '}
            <strong>
              {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
            </strong>
            .
          </p>
          <p className="text-sm text-gray-500">
            Los gastos que ya existan en el mes actual (por descripción) no se duplicarán.
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowCopyModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCopyRecurring}
              disabled={copyRecurring.isPending}
              className="flex-1"
            >
              {copyRecurring.isPending ? 'Copiando...' : 'Copiar Gastos'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Detalle de una CATEGORÍA (click en las tarjetas de categorías) */}
      <Modal
        isOpen={categoryDetail !== null}
        onClose={closeCategoryDetail}
        title={categoryDetail?.label || 'Detalle'}
        size="xl"
      >
        {categoryDetail && (
          <div className="space-y-4">
            {/* Período (chips) */}
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'month' as const, label: `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}` },
                { key: 'year' as const, label: `Año ${selectedYear}` },
                ...(categoryDetail.kind === 'expense'
                  ? [{ key: 'all' as const, label: 'Todo' }]
                  : []),
              ]).map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setDetailPeriod(p.key)}
                  className={clsx(
                    'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                    detailPeriod === p.key
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* --- SALIDAS DE CAJA --- */}
            {categoryDetail.kind === 'withdrawal' && (
              loadingWithdrawalDetail ? (
                <p className="text-center py-10 text-gray-500">Cargando movimientos...</p>
              ) : withdrawalDetail && withdrawalDetail.items.length > 0 ? (
                <>
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{withdrawalDetail.label}</p>
                      <p className="text-xs text-gray-500">{withdrawalDetail.count} movimiento(s)</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">
                      {formatPrice(withdrawalDetail.total)}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={descargarSalidasDeCaja}
                    title="Baja las salidas de caja de este periodo"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" />
                    Descargar
                  </button>

                  <div className="max-h-80 overflow-y-auto space-y-2">
                    {withdrawalDetail.items.map((it) => (
                      <div
                        key={it.id}
                        className="flex items-start justify-between gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2.5 hover:bg-gray-50 transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900 break-words">
                            {it.description || it.recipient || it.label}
                          </p>
                          <p className="text-xs text-gray-500">
                            {it.date ? new Date(it.date).toLocaleDateString('es-AR') : '—'}
                            {it.recipient ? ` · ${it.recipient}` : ''}
                            {it.payment_method
                              ? ` · ${paymentMethodLabel(it.payment_method)}`
                              : ''}
                          </p>
                        </div>
                        <span className="text-sm font-semibold text-gray-900 whitespace-nowrap tabular-nums">
                          {formatPrice(it.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-center py-10 text-gray-500">
                  No hay movimientos en este período.
                </p>
              )
            )}

            {/* --- GASTOS FIJOS --- */}
            {categoryDetail.kind === 'expense' && (
              loadingExpenseDetail ? (
                <p className="text-center py-10 text-gray-500">Cargando gastos...</p>
              ) : expenseDetailItems.length > 0 ? (
                <>
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{categoryDetail.label}</p>
                      <p className="text-xs text-gray-500">{expenseDetailItems.length} gasto(s)</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">
                      {formatPrice(expenseDetailTotal)}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={descargarGastosDeCategoria}
                    title="Baja los gastos de esta categoria en el periodo elegido"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" />
                    Descargar
                  </button>

                  {detailPeriod !== 'month' && expenseDetailByMonth.length > 1 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Por mes</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {expenseDetailByMonth.map((m) => (
                          <div
                            key={`${m.year}-${m.month}`}
                            className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                          >
                            <p className="text-xs text-gray-500">
                              {MONTH_NAMES[m.month - 1]} {m.year}
                            </p>
                            <p className="text-sm font-bold text-gray-900 tabular-nums">
                              {formatPrice(m.total)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="max-h-80 overflow-y-auto space-y-2">
                    {expenseDetailItems.map((exp) => (
                      <div
                        key={exp.id}
                        className="flex items-start justify-between gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2.5 hover:bg-gray-50 transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900 break-words">
                            {exp.description || exp.category?.name || 'Gasto'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {MONTH_NAMES[exp.month - 1]} {exp.year}
                            {exp.is_paid ? ' · Pagado' : ' · Pendiente'}
                          </p>
                        </div>
                        <span
                          className={clsx(
                            'text-sm font-semibold whitespace-nowrap tabular-nums',
                            exp.is_paid ? 'text-green-700' : 'text-orange-700'
                          )}
                        >
                          {formatPrice(exp.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-center py-10 text-gray-500">
                  No hay gastos en este período.
                </p>
              )
            )}

            <div className="flex justify-end pt-1">
              <Button variant="secondary" onClick={closeCategoryDetail}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Detalle de movimientos del MES (click en las tarjetas de arriba) */}
      <Modal
        isOpen={showMonthDetail !== null}
        onClose={() => setShowMonthDetail(null)}
        title={
          showMonthDetail === 'ventas' ? `Ventas de ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}` :
          showMonthDetail === 'costo' ? `Gastos variables de ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}` :
          showMonthDetail === 'gastos' ? `Gastos fijos de ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}` :
          `Resultado de ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`
        }
        size="2xl"
      >
        <div className="space-y-4">
          {/* --- VENTAS --- */}
          {showMonthDetail === 'ventas' && (
            loadingMonthSales ? (
              <p className="text-center py-8 text-gray-500">Cargando ventas...</p>
            ) : monthSales && monthSales.sales.length > 0 ? (
              <>
                {/* Filtro por rango de días */}
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Desde</label>
                    <input
                      type="date"
                      value={salesFrom}
                      min={`${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`}
                      onChange={(e) => setSalesFrom(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                    <input
                      type="date"
                      value={salesTo}
                      onChange={(e) => setSalesTo(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {salesRangeActive && (
                    <button
                      type="button"
                      onClick={() => { setSalesFrom(''); setSalesTo(''); }}
                      className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      Limpiar
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={descargarVentas}
                    disabled={filteredMonthSales.rows.length === 0}
                    title="Baja las ventas que se estan viendo, con el rango de dias aplicado"
                    className="sm:ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    Descargar
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-green-700">Efectivo</p>
                    <p className="text-sm font-bold text-green-800">{formatPrice(filteredMonthSales.byPm.cash || 0)}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-700">Tarjeta</p>
                    <p className="text-sm font-bold text-blue-800">{formatPrice(filteredMonthSales.byPm.card || 0)}</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <p className="text-xs text-purple-700">Transferencia</p>
                    <p className="text-sm font-bold text-purple-800">{formatPrice(filteredMonthSales.byPm.transfer || 0)}</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Ventas</p>
                    <p className="text-sm font-bold text-gray-700">{filteredMonthSales.count}</p>
                  </div>
                </div>

                {filteredMonthSales.rows.length === 0 ? (
                  <p className="text-center py-6 text-gray-500">No hay ventas en ese rango de días.</p>
                ) : (
                  <div className="max-h-80 overflow-y-auto overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Cliente</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Items</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Pago</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredMonthSales.rows.map((sale) => (
                          <tr key={sale.sale_id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                              {new Date(sale.date).toLocaleDateString('es-AR')}
                            </td>
                            <td className="px-3 py-2 text-gray-900">{sale.owner_name || '-'}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{sale.items_count}</td>
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                              {paymentMethodLabel(sale.payment_method)}
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-blue-700 whitespace-nowrap">
                              {formatPrice(sale.total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="flex justify-between items-center border-t pt-3">
                  <span className="text-sm text-gray-600">
                    {salesRangeActive ? 'Total del rango' : 'Total del mes'}
                  </span>
                  <span className="text-lg font-bold text-blue-700">{formatPrice(filteredMonthSales.total)}</span>
                </div>
              </>
            ) : (
              <p className="text-center py-8 text-gray-500">No hay ventas registradas en este mes.</p>
            )
          )}

          {/* --- GASTOS VARIABLES (costo de productos vendidos) --- */}
          {showMonthDetail === 'costo' && (
            loadingMonthCost ? (
              <p className="text-center py-8 text-gray-500">Cargando detalle...</p>
            ) : (
              <>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-xs text-orange-700">Salidas de caja del mes (gastos variables)</p>
                  <p className="text-lg font-bold text-orange-800">{formatPrice(summary?.total_variable_expenses || 0)}</p>
                </div>

                {withdrawalBreakdown && withdrawalBreakdown.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Salidas de caja por categoría</p>
                    <div className="space-y-1">
                      {withdrawalBreakdown.map((cat, idx) => (
                        <div key={`${cat.category}-${idx}`} className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2">
                          <span className="text-sm text-gray-700">{cat.label}</span>
                          <span className="text-sm font-semibold text-gray-900">{formatPrice(cat.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {monthCost && monthCost.products.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2 mt-4">
                      Costo de productos vendidos: {formatPrice(monthCost.total_cost)}
                    </p>
                    <div className="max-h-60 overflow-y-auto overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Cant.</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Costo</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {monthCost.products.map((p) => (
                            <tr key={p.product_id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-900">{p.product_name}</td>
                              <td className="px-3 py-2 text-center text-gray-600">{p.quantity_sold}</td>
                              <td className="px-3 py-2 text-right font-medium text-orange-700 whitespace-nowrap">
                                {formatPrice(p.total_cost)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )
          )}

          {/* --- GASTOS FIJOS --- */}
          {showMonthDetail === 'gastos' && (
            expenses.length > 0 ? (
              <>
                <div className="max-h-80 overflow-y-auto">
                  <div className="space-y-1">
                    {expenses.map((exp) => (
                      <div key={exp.id} className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2 gap-3">
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900 break-words">
                            {exp.description || exp.category?.name || 'Gasto'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {exp.category?.name}
                            {exp.is_paid ? ' · Pagado' : ' · Pendiente'}
                          </p>
                        </div>
                        <span className={clsx(
                          'text-sm font-semibold whitespace-nowrap',
                          exp.is_paid ? 'text-green-700' : 'text-orange-700'
                        )}>
                          {formatPrice(exp.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap justify-between items-center gap-3 border-t pt-3">
                  <button
                    type="button"
                    onClick={descargarGastosDelMes}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" />
                    Descargar
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Total gastos fijos</span>
                    <span className="text-lg font-bold text-red-700">{formatPrice(summary?.total_fixed_expenses || 0)}</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-center py-8 text-gray-500">No hay gastos fijos cargados en este mes.</p>
            )
          )}

          {/* --- RESULTADO NETO --- */}
          {showMonthDetail === 'resultado' && summary && (
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-600">Ventas del mes</span>
                <span className="text-sm font-semibold text-green-700">+{formatPrice(summary.total_income)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-600">Gastos variables</span>
                <span className="text-sm font-semibold text-red-600">-{formatPrice(summary.total_variable_expenses)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-600">Gastos fijos</span>
                <span className="text-sm font-semibold text-red-600">-{formatPrice(summary.total_fixed_expenses)}</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="font-semibold text-gray-900">Resultado neto</span>
                <span className={clsx('text-xl font-bold', isPositive ? 'text-green-700' : 'text-red-700')}>
                  {formatPrice(netResult)}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Tocá cada tarjeta del resumen para ver el detalle de cada concepto.
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowMonthDetail(null)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Detalle Resumen Anual */}
      <Modal
        isOpen={showYearlyDetailModal !== null}
        onClose={() => setShowYearlyDetailModal(null)}
        title={
          showYearlyDetailModal === 'ventas' ? `Detalle de Ventas ${selectedYear}` :
          showYearlyDetailModal === 'costo' ? `Detalle de Gastos Variables ${selectedYear}` :
          showYearlyDetailModal === 'gastos' ? `Detalle de Gastos Fijos ${selectedYear}` :
          `Detalle de Resultado Neto ${selectedYear}`
        }
        size="xl"
      >
        {yearlySummary && (
          <div className="space-y-4">
            {/* Resumen mensual - siempre visible */}
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Resumen por Mes</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mes</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {showYearlyDetailModal === 'ventas' && 'Ventas'}
                        {showYearlyDetailModal === 'costo' && 'Gastos Variables'}
                        {showYearlyDetailModal === 'gastos' && 'Gastos Fijos'}
                        {showYearlyDetailModal === 'resultado' && 'Resultado Neto'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {yearlySummary.months.map((month) => {
                      const value = showYearlyDetailModal === 'ventas' ? month.total_income :
                                    showYearlyDetailModal === 'costo' ? month.total_variable_expenses :
                                    showYearlyDetailModal === 'gastos' ? month.total_expenses :
                                    month.net_result;
                      const isNegative = value < 0;

                      return (
                        <tr key={month.month} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {MONTH_NAMES[month.month - 1]}
                          </td>
                          <td className={clsx(
                            'px-4 py-3 text-sm font-medium text-right',
                            showYearlyDetailModal === 'ventas' && 'text-blue-700',
                            showYearlyDetailModal === 'costo' && 'text-orange-700',
                            showYearlyDetailModal === 'gastos' && 'text-red-700',
                            showYearlyDetailModal === 'resultado' && (isNegative ? 'text-rose-700' : 'text-emerald-700')
                          )}>
                            {formatPrice(value)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">TOTAL</td>
                      <td className={clsx(
                        'px-4 py-3 text-sm font-bold text-right',
                        showYearlyDetailModal === 'ventas' && 'text-blue-700',
                        showYearlyDetailModal === 'costo' && 'text-orange-700',
                        showYearlyDetailModal === 'gastos' && 'text-red-700',
                        showYearlyDetailModal === 'resultado' && (yearlySummary.net_result < 0 ? 'text-rose-700' : 'text-emerald-700')
                      )}>
                        {formatPrice(
                          showYearlyDetailModal === 'ventas' ? yearlySummary.total_income :
                          showYearlyDetailModal === 'costo' ? yearlySummary.total_variable_expenses :
                          showYearlyDetailModal === 'gastos' ? yearlySummary.total_expenses :
                          yearlySummary.net_result
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Desglose de Ventas */}
            {showYearlyDetailModal === 'ventas' && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-2">Desglose de Ventas</h4>
                {loadingSales ? (
                  <div className="text-center py-4 text-gray-500">Cargando ventas...</div>
                ) : salesBreakdown ? (
                  <div className="space-y-4">
                    {/* Por método de pago */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-xs text-green-600">Efectivo</p>
                        <p className="text-sm font-bold text-green-700">
                          {formatPrice(salesBreakdown.by_payment_method.cash || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <p className="text-xs text-blue-600">Tarjeta</p>
                        <p className="text-sm font-bold text-blue-700">
                          {formatPrice(salesBreakdown.by_payment_method.card || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg text-center">
                        <p className="text-xs text-purple-600">Transferencia</p>
                        <p className="text-sm font-bold text-purple-700">
                          {formatPrice(salesBreakdown.by_payment_method.transfer || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-600">Total Ventas</p>
                        <p className="text-sm font-bold text-gray-700">{salesBreakdown.sales_count}</p>
                      </div>
                    </div>

                    {/* Lista de ventas (últimas 20) */}
                    <div className="max-h-60 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Cliente</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Items</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Pago</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {salesBreakdown.sales.slice(0, 50).map((sale) => (
                            <tr key={sale.sale_id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-600">
                                {new Date(sale.date).toLocaleDateString('es-AR')}
                              </td>
                              <td className="px-3 py-2 text-gray-900">
                                {sale.owner_name || '-'}
                              </td>
                              <td className="px-3 py-2 text-center text-gray-600">
                                {sale.items_count}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {paymentMethodLabel(sale.payment_method)}
                              </td>
                              <td className="px-3 py-2 text-right font-medium text-blue-700">
                                {formatPrice(sale.total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {salesBreakdown.sales.length > 50 && (
                        <p className="text-center text-xs text-gray-500 py-2">
                          Mostrando 50 de {salesBreakdown.sales.length} ventas
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Desglose de Costo de Productos */}
            {showYearlyDetailModal === 'costo' && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-2">Desglose por Producto</h4>
                {loadingProductCost ? (
                  <div className="text-center py-4 text-gray-500">Cargando productos...</div>
                ) : productCostBreakdown ? (
                  <div className="max-h-80 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Cant. Vendida</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Costo Unit.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Costo Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {productCostBreakdown.products.map((product) => (
                          <tr key={product.product_id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-900">{product.product_name}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{product.quantity_sold}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{formatPrice(product.unit_cost)}</td>
                            <td className="px-3 py-2 text-right font-medium text-orange-700">{formatPrice(product.total_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100">
                        <tr>
                          <td colSpan={3} className="px-3 py-2 text-sm font-bold text-gray-900">TOTAL COSTO</td>
                          <td className="px-3 py-2 text-right font-bold text-orange-700">
                            {formatPrice(productCostBreakdown.total_cost)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    {productCostBreakdown.products.length === 0 && (
                      <p className="text-center text-gray-500 py-4">No hay productos vendidos en este período</p>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Explicación del cálculo para Resultado Neto */}
            {showYearlyDetailModal === 'resultado' && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-700 mb-3">Cálculo del Resultado Neto</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas totales</span>
                    <span className="font-medium text-blue-700">{formatPrice(yearlySummary.total_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">- Costo de productos</span>
                    <span className="font-medium text-gray-500">-{formatPrice(yearlySummary.total_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">- Gastos variables (salidas de caja)</span>
                    <span className="font-medium text-orange-700">-{formatPrice(yearlySummary.total_variable_expenses)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600">= Ganancia bruta</span>
                    <span className="font-medium text-green-700">{formatPrice(yearlySummary.total_profit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">- Gastos fijos</span>
                    <span className="font-medium text-red-700">-{formatPrice(yearlySummary.total_expenses)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 text-base">
                    <span className="font-bold text-gray-900">= Resultado Neto</span>
                    <span className={clsx(
                      'font-bold',
                      yearlySummary.net_result >= 0 ? 'text-emerald-700' : 'text-rose-700'
                    )}>
                      {formatPrice(yearlySummary.net_result)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="secondary" onClick={() => setShowYearlyDetailModal(null)}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>
      </>
      )}
    </div>
  );
}
